import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// PR-JWT-COOKIE: withCredentials makes axios send the httpOnly auth_token
// cookie on every request and accept Set-Cookie back from the server. The
// previous Authorization header injection is gone -- the cookie travels
// automatically and JS can't read it (which is the whole point).
const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
});

// Response interceptor: when the backend returns a zod validation 400, the
// payload looks like:
//   { error: 'Invalid request', details: [{ path, message }, ...] }
// Without flattening, every caller's catch block would surface 'Invalid
// request', which tells the user nothing. This rewrites response.data.error
// in place to a readable summary so the existing 16+ callers using
// err.response?.data?.error keep working with no code changes.
//
// Format: 'field1: msg1; field2: msg2'. Root-level issues (empty path —
// e.g. .strict() unknown-key rejections) render as just the message.
//
// Non-validation errors (no details array, or 401/403/404/500) pass through
// untouched so we don't disturb existing behavior.
// Subscriber for "a request just got a 403" -- lets App.js register a
// callback (set once on mount) that refetches /auth/me whenever ANY API
// call anywhere in the app comes back 403. This exists specifically to
// close a real gap in the ToS re-acceptance gate: attachOrganizer's
// tos_version check is live on every backend request with no caching, so
// the moment a ToS/Privacy change is published, an already-logged-in
// organizer's very next organizer-scoped API call starts 403ing --
// but the FRONTEND's tos_status (on the user object) is only fetched
// once on page mount, with no polling and no refetch on client-side tab
// navigation. Without this, someone with an already-open tab would see
// the old scattered-403-errors experience instead of the new gate, right
// up until they happened to refresh or re-login. Refetching on any 403
// is deliberately not filtered to tos-specific error messages -- it's
// cheap, idempotent, and harmless even when the 403 was for an unrelated
// reason (e.g. a real ownership check), so there's no need to parse
// error text to decide whether to fire it.
let onForbiddenHandler = null;
export function setOnForbiddenHandler(fn) {
    onForbiddenHandler = fn;
}

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const data    = error.response?.data;
        const details = data?.details;
        if (Array.isArray(details) && details.length > 0) {
            const summary = details
                .map(d => d.path ? `${d.path}: ${d.message}` : d.message)
                .join('; ');
            data.error = summary;
        }
        if (error.response?.status === 403 && onForbiddenHandler) {
            onForbiddenHandler();
        }
        return Promise.reject(error);
    }
);

export const login = (email, password) =>
    api.post('/auth/login', { email, password });

// PR-JWT-COOKIE: rehydrate the user on app mount. Returns the participant
// (and 200) if the cookie is present and valid; 401 otherwise. Replaces
// the old localStorage-decode-on-mount trick in App.js.
export const getCurrentUser = () =>
    api.get('/auth/me');

export const updateOrganizerName = (display_name) =>
    api.put('/auth/organizer/me', { display_name });

export const checkSlugAvailability = (slug) =>
    api.get('/auth/organizer/slug-check', { params: { slug } });

export const updateOrganizerSlug = (slug) =>
    api.put('/auth/organizer/me/slug', { slug });

export const updateOrganizerBranding = (primary, accent) =>
    api.put('/auth/organizer/me/branding', { primary, accent });

export const signupOrganizer = ({ name, email, password }) =>
    api.post('/auth/signup-organizer', { name, email, password });

export const verifyOrganizer = (token) =>
    api.post('/auth/verify-organizer', { token });

// PR-JWT-COOKIE: clear the auth cookie server-side. Frontend should
// follow up by clearing local user state.
export const logout = () =>
    api.post('/auth/logout');

export const getLeaderboard = (poolId) =>
    api.get(`/leaderboard/${poolId}`);

export const getPools = (tournamentId) =>
    api.get('/pools', { params: { tournamentId } });

export const getPoolEntries = (poolId) =>
    api.get(`/pools/${poolId}/entries`);

export const getPoolStats = (poolId) =>
    api.get(`/pools/${poolId}/stats`);

export const clearPoolData = (poolId) =>
    api.delete(`/pools/${poolId}/data`);

export const deletePool = (poolId) =>
    api.delete(`/pools/${poolId}`);

// organizerId: optional, platform-admin-only filter (ignored server-side
// for regular organizers, who are always scoped to their own pools
// regardless of what's passed here -- see GET /participants).
export const getParticipants = (organizerId) =>
    api.get(organizerId ? `/participants?organizer_id=${organizerId}` : '/participants');

export const getParticipantMemberships = (id) =>
    api.get(`/participants/${id}/memberships`);

export const bulkDeleteParticipants = (participantIds) =>
    api.delete('/participants', { data: { participant_ids: participantIds } });

export const addParticipant = (name, email, password, is_admin) =>
    api.post('/participants', { name, email, password, is_admin });

export const resetPassword = (id, password) =>
    api.put(`/participants/${id}/password`, { password });

// PR-R3: self-service change password. Distinct from resetPassword above
// (which is admin-only). The backend route is PUT /participants/me/password
// and uses the JWT to identify the user -- no id needed in the URL.
export const changePassword = (current_password, new_password) =>
    api.put('/participants/me/password', { current_password, new_password });

// PR-FP: forgot-password flow. Both endpoints are public (no auth cookie
// needed -- the user can't log in, that's the point).
export const forgotPassword = (email) =>
    api.post('/auth/forgot-password', { email });

export const resetPasswordWithToken = (token, new_password) =>
    api.post('/auth/reset-password', { token, new_password });

export const getPicks = (entryId) =>
    api.get(`/picks/${entryId}`);

// Fetch pick distribution for a set of games within a pool. gameIds is
// an array of game IDs; serialized to a comma-separated query string.
// Returns { [gameId]: { team1_count, team2_count, total } }.
export const getPickDistribution = (poolId, gameIds) =>
    api.get('/picks/distribution', { params: { poolId, gameIds: gameIds.join(',') } });

// Feedback
export const submitFeedback  = (data) => api.post('/feedback', data);
export const getFeedback     = ()     => api.get('/feedback');
export const updateFeedback  = (id, data) => api.put(`/feedback/${id}`, data);

// App config
export const getAppConfig    = ()     => api.get('/app-config');
export const updateAppConfig = (data) => api.put('/app-config', data);

export const getLegalDocument     = (docType)          => api.get(`/admin/legal/${docType}`);
export const publishLegalDocument = (docType, content) => api.put(`/admin/legal/${docType}`, { content });

export const getAuditLog = (params) =>
    api.get('/audit-log', { params });

export const getMyAuditLog = (params) =>
    api.get('/audit-log/mine', { params });

export const getOrganizers = () =>
    api.get('/admin/organizers');

export const updateOrganizerStatus = (id, status) =>
    api.put(`/admin/organizers/${id}/status`, { status });

export const assignOrganizerPlan = (id, planId, markPaid = true) =>
    api.put(`/admin/organizers/${id}/plan`, { plan_id: planId, mark_paid: markPaid });

export const sendInvites = (emails) =>
    api.post('/registrations/invite', { emails });

export const requestOrganizer = () =>
    api.post('/participants/me/request-organizer');

export const createCheckoutSession = (planId) =>
    api.post('/billing/checkout', { planId });

export const acceptTos = (token) =>
    api.post('/auth/accept-tos', { token });

// Session-based re-acceptance for the post-login gate (App.js) -- no
// token, just uses the current session to accept for whoever's logged
// in. See src/routes/auth.js POST /auth/organizer/me/accept-tos.
export const acceptOrganizerTosSession = () =>
    api.post('/auth/organizer/me/accept-tos');

export const getTermsOfService = () =>
    api.get('/legal/terms');

export const getOrganizerBySlug = (slug) =>
    api.get(`/organizers/by-slug/${slug}`);

export const getPrivacyPolicy = () =>
    api.get('/legal/privacy');

// Version + published_at for both documents -- used to render an
// accurate "Last updated / Version" byline dynamically instead of it
// being hand-typed text inside the document content (see the comment on
// GET /legal/meta in src/routes/legal.js for the bug this fixes).
export const getLegalMeta = () =>
    api.get('/legal/meta');

// Dev tools (platform-owner-only, local/staging only -- see
// src/routes/dev-tools.js and middleware.js's devToolsOnly). pingDevTools
// intentionally never throws its own custom error -- a 404 here just
// means dev tools aren't enabled in this environment, which is the
// expected, correct state in production.
export const pingDevTools = () =>
    api.get('/dev-tools/ping');

export const clearDevToolsLogs = () =>
    api.delete('/dev-tools/logs');

export const getDevToolsOrganizers = () =>
    api.get('/dev-tools/scenarios/organizers');

export const createDevToolsScenario = (payload) =>
    api.post('/dev-tools/scenarios/organizer', payload);

export const deleteDevToolsScenario = (organizerId) =>
    api.delete(`/dev-tools/scenarios/organizer/${organizerId}`);

// Direct name/email/password edit for any participant. Bundled onto the
// dev-tools-only route purely because email has no other edit path in
// the app -- name/password already work via updateParticipant /
// resetPassword above for platform admins, this is not a replacement
// for those.
export const updateDevToolsParticipant = (participantId, fields) =>
    api.put(`/dev-tools/participants/${participantId}`, fields);

// Dry-run check for pending migrations (read-only, touches no data) and
// the real apply. Both run against whatever DB this backend instance is
// itself already connected to -- never a separate target to configure.
export const getDevToolsMigrationStatus = () =>
    api.get('/dev-tools/migrations/status');

export const runDevToolsMigrations = () =>
    api.post('/dev-tools/migrations/run');

export const impersonateOrganizer = (organizerId) =>
    api.post(`/admin/impersonate/${organizerId}`);

export const stopImpersonating = () =>
    api.post('/admin/stop-impersonating');

export const sendCommunication = (data) =>
    api.post('/communications', data);

export const getCommunications = (params) =>
    api.get('/communications', { params });

// Returns only participants actually eligible to receive a communication
// for the given tournament (and pool, if narrowed) -- i.e. those actually
// enrolled via pool_entries, not every participant on the platform. Used
// by the Communications compose picker so an organizer can't select
// someone who'd just bounce back with "No valid recipients found".
export const getCommunicationsRecipients = (tournamentId, poolId) =>
    api.get('/communications/recipients', { params: { tournament_id: tournamentId, pool_id: poolId || undefined } });

export const submitPick = (entry_id, game_id, picked_team_id) =>
    api.post('/picks', { entry_id, game_id, picked_team_id });

// PR-TIEBREAKER B: save (or clear) a championship score prediction on
// an entry. Body fields are independently optional/nullable, mirroring
// the backend: absent = keep, explicit null = clear, present = set.
export const setTiebreaker = (entry_id, body) =>
    api.put(`/picks/entries/${entry_id}/tiebreaker`, body);

export const getGames = (tournamentId, status) =>
    api.get('/games', { params: { tournamentId, status } });

export const recordResult = (gameId, winner_id) =>
    api.put(`/games/${gameId}/result`, { winner_id });

// Results-sync feature: read-only comparison against the canonical
// (is_template=true) tournament for the same year/gender, and applying
// a synced result -- which is just the same PUT /:id/result route,
// with synced_from_canonical asserted so the backend can (after its own
// independent verification) attribute the audit entry to the platform
// owner instead of the organizer who clicked sync.
export const getSyncPreview = (tournamentId) =>
    api.get(`/games/sync-preview?tournament_id=${tournamentId}`);

export const syncGameResult = (gameId, winner_id) =>
    api.put(`/games/${gameId}/result`, { winner_id, synced_from_canonical: true });

export const updateParticipant = (id, name) =>
    api.put(`/participants/${id}`, { name });

export const updateParticipantRole = (id, role) =>
    api.put(`/participants/${id}/role`, { role });

export const deleteParticipant = (id) =>
    api.delete(`/participants/${id}`);

export const deletePoolParticipants = (poolId) =>
    api.delete(`/pools/${poolId}/participants`);

export const deletePoolEntry = (poolId, entryId) =>
    api.delete(`/pools/${poolId}/participants/${entryId}`);

export const getMyEntries = () =>
    api.get('/pools/my-entries');

export const getIncompletePicks = (tournamentId) =>
    api.get(`/pools/tournament/${tournamentId}/incomplete-picks`);

export const getRounds = (tournamentId) =>
    api.get('/rounds', { params: { tournamentId } });

export const getTournaments = () =>
    api.get('/tournaments');

export const getTournamentTemplates = () =>
    api.get('/tournaments', { params: { templates: true } });

export const cloneTournament = (templateId, { name, year, gender }) =>
    api.post(`/tournaments/clone/${templateId}`, { name, year, gender });

export const createTournament = (data) =>
    api.post('/tournaments', data);

export const updateTournament = (id, data) =>
    api.put(`/tournaments/${id}`, data);

export const createRounds = (tournamentId, rounds) =>
    api.post(`/rounds/tournament/${tournamentId}`, { rounds });

export const updateRound = (id, data) =>
    api.put(`/rounds/${id}`, data);

export const getTeams = () =>
    api.get('/teams');

export const bulkImportTeams = (teams) =>
    api.post('/teams/bulk', { teams });

export const updateTeam = (id, data) =>
    api.put(`/teams/${id}`, data);

export const deleteTeam = (id) =>
    api.delete(`/teams/${id}`);

export const getTeamUsage = (id) =>
    api.get(`/teams/${id}/usage`);

export const createPool = (data) =>
    api.post('/pools', data);

export const updatePoolSettings = (poolId, data) =>
    api.put(`/pools/${poolId}/settings`, data);

export const updateScoringRules = (poolId, rules) =>
    api.put(`/pools/${poolId}/scoring-rules`, { rules });

export const enrollParticipants = (poolId, participant_ids) =>
    api.post(`/pools/${poolId}/enroll`, { participant_ids });

export const generateRound = (tournament_id, round_number) =>
    api.post('/games/generate-round', { tournament_id, round_number });

export const getTournamentTeams = (tournamentId) =>
    api.get(`/tournaments/${tournamentId}/tournament-teams`);

export const addTournamentTeam = (tournamentId, data) =>
    api.post(`/tournaments/${tournamentId}/tournament-teams`, data);

export const removeTournamentTeam = (tournamentId, ttId) =>
    api.delete(`/tournaments/${tournamentId}/tournament-teams/${ttId}`);

// Auto-fetch pre-fill: NEVER writes anything by itself. Takes a date
// RANGE (inclusive) -- the person doesn't need to know the exact Round
// of 64 dates, just roughly the week; a day with no First Round games
// simply contributes nothing.
export const fetchSeedImportPreview = (tournamentId, startDate, endDate) =>
    api.get(`/tournaments/${tournamentId}/seed-import/fetch-external`, {
        params: { start_date: startDate, end_date: endDate }
    });

// The actual commit -- assignments: [{ team_id, seed, region }, ...].
// regionDisplayNames (optional) persists a non-classic region mapping
// (e.g. women's site-based names discovered by fetchSeedImportPreview)
// onto the tournament in the same request.
export const bulkAssignTournamentTeams = (tournamentId, assignments, regionDisplayNames) => {
    const body = { assignments };
    if (regionDisplayNames) body.region_display_names = regionDisplayNames;
    return api.post(`/tournaments/${tournamentId}/tournament-teams/bulk`, body);
};

export const deleteTournament = (id) =>
    api.delete(`/tournaments/${id}`);

export const resetTournamentGames = (tournamentId) =>
    api.delete(`/games/tournament/${tournamentId}/reset`);

export const getHistoricalChampionships = (gender) =>
    api.get('/history/championships', { params: gender ? { gender } : {} });

export const getHistoricalSemifinals = (year, gender) =>
    api.get('/history/semifinals', { params: { year, gender } });

export const getScoringRules = (poolId) =>
    api.get(`/pools/${poolId}/scoring-rules`);

// ─────────────────────────────────────────────────────────────────────
// Registrations (PR-R1+R2)
// ─────────────────────────────────────────────────────────────────────

// Public: tournaments + pools that the registration form can offer.
// No auth required (the route itself is public).
export const getRegistrationOptions = (organizerId) =>
    api.get('/registrations/options', { params: { organizerId } });

// Public: submit a registration request. Caller passes tournament_id as
// a number and pool_ids as an array of numbers (matches backend zod schema).
export const submitRegistration = (name, email, tournament_id, pool_ids) =>
    api.post('/registrations', { name, email, tournament_id, pool_ids });

// Admin: list registration requests, optionally filtered by status
// ('pending' | 'approved' | 'all'). Defaults to 'pending' on the backend.
export const getRegistrations = (status) =>
    api.get('/registrations', { params: status ? { status } : {} });

// Admin: approve a pending request. Creates participant + enrolls + emails.
// notes is optional admin commentary stored on the row.
export const approveRegistration = (id, notes) =>
    api.post(`/registrations/${id}/approve`, notes ? { notes } : {});

// Admin: reject (delete) a pending request.
export const rejectRegistration = (id) =>
    api.post(`/registrations/${id}/reject`);

export default api;